<template>
  <LoadingWrapper :state="status">
    <BaseTabGroup v-if="impulse" :tab-items="lessonImpulseTabItems">
      <FullScreenLayoutContentHero>
        <template #header>
          <div
            class="border-outline-variant bg-surface shadow-1 shadow-outline-variant flex items-center gap-4 px-6 py-2"
          >
            <BackButton backup-route-name="impulse-library" :title="$t('common.back')" />
            <div class="rounded-small aspect-4/3 h-10 overflow-hidden">
              <img :src="impulse.image" :alt="impulse.title" class="h-full w-full object-cover" />
            </div>
            <h1 class="text-headline-small grow">{{ impulse.title }}</h1>
            <div
              v-if="impulse.myLessonCounts.running > 0 || impulse.myLessonCounts.finished > 0"
              class="flex shrink-0 flex-row items-center gap-3"
            >
              <RouterLink
                v-if="impulse.myLessonCounts.running > 0"
                :to="{ name: 'impulse-library' }"
                class="text-label-large text-primary"
              >
                {{ $t('lumia.lesson.myCountRunning', { count: impulse.myLessonCounts.running }) }}
              </RouterLink>
            </div>
            <LessonImpulseBookmarkButton :impulse="impulse" size="M" />
            <BaseTextButton
              v-if="canStartLesson"
              color="filled"
              icon="play_arrow"
              shape="square"
              size="M"
              @click="openStartDialog"
              >{{ $t('lumia.lesson.start') }}</BaseTextButton
            >
          </div>
        </template>
        <template #hero>
          <div class="flex w-full min-w-0 flex-col">
            <div class="grid grid-cols-1 gap-4 md:grid-cols-12">
              <div
                class="bg-surface-container rounded-extra-large flex flex-col p-10 md:col-span-7"
              >
                <div class="-mx-4 -mt-4 flex flex-row">
                  <BackButton backup-route-name="impulse-library" :title="$t('common.back')" />
                  <LessonImpulseBookmarkButton
                    :impulse="impulse"
                    size="S"
                    color="tonal"
                    class="ml-auto"
                  />
                </div>
                <h1 class="text-display-medium mb-8">{{ impulse.title }}</h1>
                <div class="flex w-full flex-row flex-wrap gap-1 overflow-hidden">
                  <BaseChip
                    v-for="kompetenz in impulse.ueberfachlicheKompetenzen"
                    :key="kompetenz.id"
                    :color="colorForUeberfachlicheKompetenz(kompetenz)"
                  >
                    {{ kompetenz.title }}
                  </BaseChip>
                  <BaseChip v-if="impulse.standortbestimmung.length">Standortbestimmung</BaseChip>
                </div>
                <div class="text-body-large mt-4 flex flex-row flex-wrap items-center">
                  <LessonImpulseSubjects :subjects="impulse.subjects" class="font-medium" />
                  ・
                  <span class="inline-flex flex-row items-center gap-1">
                    <BaseIcon size="18">schedule</BaseIcon
                    >{{ $t('lumia.numberOfLessons', { count: impulse.numberOfLessons }) }}
                  </span>
                  ・
                  <span v-if="gradeClassLabel(impulse.grades)">
                    {{ gradeClassLabel(impulse.grades) }}
                  </span>
                  <span v-if="impulse.context.socialForm">
                    ・{{ impulse.context.socialForm.label }}
                  </span>
                  <span
                    v-if="impulse.myLessonCounts.finished > 0"
                    v-tooltip="
                      `${$t('lumia.lesson.myCountFinished', { count: impulse.myLessonCounts.finished })}`
                    "
                    class="inline-flex flex-row items-center gap-0.5"
                  >
                    ・ <BaseIcon size="18">repeat</BaseIcon> {{ impulse.myLessonCounts.finished }}
                  </span>
                </div>
                <div class="mt-6 -mr-2 -mb-2 flex flex-row items-center gap-1">
                  <RouterLink
                    v-if="impulse.myLessonCounts.running > 0"
                    :to="{ name: 'impulse-library' }"
                  >
                    <BaseTextButton color="filled" size="M">
                      {{
                        $t('lumia.lesson.myCountRunning', { count: impulse.myLessonCounts.running })
                      }}
                    </BaseTextButton>
                  </RouterLink>
                  <BaseIconButton
                    v-if="canStartLesson && impulse.myLessonCounts.running != 0"
                    icon="play_arrow"
                    color="tonalPrimary"
                    shape="square"
                    size="M"
                    @click="openStartDialog"
                  />

                  <BaseTextButton
                    v-if="canStartLesson && impulse.myLessonCounts.running === 0"
                    color="filled"
                    icon="play_arrow"
                    shape="square"
                    size="M"
                    @click="openStartDialog"
                    >{{ $t('lumia.lesson.start') }}</BaseTextButton
                  >
                </div>
              </div>
              <div class="rounded-extra-large relative overflow-hidden md:col-span-5">
                <img :src="impulse.image" :alt="impulse.title" class="h-full w-full object-cover" />
              </div>
            </div>
            <LessonImpulseTabBar />
          </div>
        </template>
        <LessonImpulseContent :impulse="impulse" />
      </FullScreenLayoutContentHero>
    </BaseTabGroup>
    <StartLessonDialog
      v-if="impulse"
      :is-open="isStartDialogOpen"
      :lesson-impulse-id="impulse.id"
      :default-title="impulse.title"
      @close="closeStartDialog"
      @started="onLessonStarted"
    />
  </LoadingWrapper>
</template>
<script lang="ts" setup>
  import { computed } from 'vue'
  import { useRouter } from 'vue-router'
  import BackButton from '@/core/components/BackButton.vue'
  import BaseTabGroup from '@/core/components/base/tab-bar/BaseTabGroup.vue'
  import FullScreenLayoutContentHero from '@/core/components/layouts/FullScreenLayoutContentHero.vue'
  import LoadingWrapper from '@/core/components/loading/LoadingWrapper.vue'
  import { useLessonImpulse } from '../composables/use-lesson-impulse'
  import { gradeClassLabel } from '../models/lesson-impulse'
  import { useLessonImpulseTabItems } from '../models/lesson-impulse-tabs'
  import LessonImpulseContent from '../components/LessonImpulseContent.vue'
  import LessonImpulseTabBar from '../components/LessonImpulseTabBar.vue'
  import BaseIcon from '@/core/components/base/icon/BaseIcon.vue'
  import LessonImpulseSubjects from '../components/LessonImpulseSubjects.vue'
  import BaseChip from '@/core/components/base/chip/BaseChip.vue'
  import { colorForUeberfachlicheKompetenz } from '../models/ueberfachliche-kompetenz-color.ts'
  import BaseTextButton from '@/core/components/base/button/text-button/BaseTextButton.vue'
  import { useDialog } from '@/core/components/base/dialog/use-dialog'
  import { usePermission } from '@/core/composables/use-permission'
  import { Permission } from '@/core/models/user'
  import StartLessonDialog from '../components/StartLessonDialog.vue'
  import BaseIconButton from '@/core/components/base/button/icon-button/BaseIconButton.vue'
  import LessonImpulseBookmarkButton from '../components/LessonImpulseBookmarkButton.vue'

  const lessonImpulseTabItems = useLessonImpulseTabItems()
  const router = useRouter()
  const { can } = usePermission()
  const canStartLesson = computed(() => can(Permission.LESSON_START))
  const {
    isOpen: isStartDialogOpen,
    openDialog: openStartDialog,
    closeDialog: closeStartDialog,
  } = useDialog()

  const { impulse, status } = useLessonImpulse()

  const onLessonStarted = (lessonId: string) => {
    closeStartDialog()
    router.push({ name: 'lessons-detail', params: { lessonId } })
  }
</script>
